import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Card,
} from "react-bootstrap";
import { notifyError, notifyTopRight } from "../common/Toaster";
import PageTitle from "../layouts/PageTitle";
import SwitchButton from "../components/ReactSwitch";
import {
  getClassifierConfig,
  updateClassifierConfig,
} from "../../services/Classifier/ClassifierService";

const ClassifierSettings = () => {
  const [killSwitch, setKillSwitch] = useState(false);
  const [confidenceThreshold, setConfidenceThreshold] = useState(70);
  const [timeout, setTimeout_] = useState(10);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await getClassifierConfig();
      const config = response.data.data;
      setKillSwitch(config.killSwitch);
      setConfidenceThreshold(config.confidenceThreshold);
      setTimeout_(config.timeout);
    } catch (error) {
      notifyError("Failed to fetch classifier config");
    } finally {
      setFetchLoading(false);
    }
  };

  const validateAndSave = async () => {
    const threshold = Number(confidenceThreshold);
    const timeoutVal = Number(timeout);

    if (!Number.isInteger(threshold) || threshold < 0 || threshold > 100) {
      notifyError("Confidence threshold must be an integer between 0 and 100");
      return;
    }

    if (!Number.isInteger(timeoutVal) || timeoutVal < 1 || timeoutVal > 60) {
      notifyError("Timeout must be an integer between 1 and 60");
      return;
    }

    setSaveLoading(true);
    try {
      await updateClassifierConfig({
        killSwitch,
        confidenceThreshold: threshold,
        timeout: timeoutVal,
      });
      notifyTopRight("Classifier config updated successfully");
    } catch (error) {
      notifyError(
        error?.response?.data?.message || "Error updating classifier config"
      );
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div>
      <Col>
        <PageTitle activeMenu="Classifier Settings" />
        <Card>
          <Card.Body>
            {fetchLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <Spinner animation="border" />
              </div>
            ) : (
              <Form>
                <Row className="mb-4">
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        Azure Kill Switch
                      </Form.Label>
                      <div>
                        <SwitchButton
                          initialValue={killSwitch}
                          onChange={(value) => setKillSwitch(value)}
                        />
                      </div>
                      <Form.Text className="text-muted">
                        OFF = both classifiers run in parallel. ON = Qwen only.
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        Qwen Confidence Threshold (%)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={confidenceThreshold}
                        onChange={(e) =>
                          setConfidenceThreshold(e.target.value)
                        }
                      />
                      <Form.Text className="text-muted">
                        Qwen routes when confidence is at or above this value
                        (0-100)
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        Classification Timeout (seconds)
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={60}
                        step={1}
                        value={timeout}
                        onChange={(e) => setTimeout_(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Maximum time to wait for both classifiers (1-60)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Button
                  variant="primary"
                  onClick={validateAndSave}
                  disabled={saveLoading}
                >
                  {saveLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Col>
    </div>
  );
};

export default ClassifierSettings;
